import { maybe, Maybe } from '@passionware/monads';
import { Nullable } from 'v5/platform/typescript';

/**
 * Layer 2 — browsing scope: list API workspace filters from resolved app scope (URL).
 *
 * @see .agents/rules/workspace-scoping-layers.md
 */

export type WorkspaceScoping = { workspaceId: Nullable<number> };

export type WithWorkspaceScoping<T> = T & WorkspaceScoping;

/** Discriminated app-scope shape (resolved or identity) for workspace stamps. */
export type WorkspaceScopingSource =
  | { level: 'organization' }
  | { level: 'workspace' | 'team'; workspaceId: number };

export const workspaceScopingQueryUtils = {
  ofGlobal: (): WorkspaceScoping => ({ workspaceId: null }),
  ofWorkspace: (workspaceId: number): WorkspaceScoping => ({ workspaceId }),
  fromScope: (scope: WorkspaceScopingSource): WorkspaceScoping => {
    if (scope.level === 'organization') {
      return workspaceScopingQueryUtils.ofGlobal();
    }
    return workspaceScopingQueryUtils.ofWorkspace(scope.workspaceId);
  }
};

/** Workspace id from browsing scope; absent on org / missing scope. */
export function workspaceIdFromScope(
  scope: Maybe<WorkspaceScopingSource>
): Maybe<number> {
  return maybe.map(scope, s =>
    s.level === 'organization' ? maybe.ofAbsent() : s.workspaceId
  );
}

export function withWorkspaceScoping<T>(
  searchQuery: T,
  scoping: WorkspaceScoping
): WithWorkspaceScoping<T> {
  return { ...searchQuery, ...scoping };
}

export function asGlobalResourceQuery<T>(
  searchQuery: T
): WithWorkspaceScoping<T> {
  return withWorkspaceScoping(
    searchQuery,
    workspaceScopingQueryUtils.ofGlobal()
  );
}
